import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { AuditEntry } from "@/lib/types";
import { Panel } from "@/ui/Panel";
import { Input } from "@/ui/Field";
import { Table, Td, Th, Tr } from "@/ui/Table";
import { EmptyState, ErrorState, TableSkeleton } from "@/ui/States";
import { absoluteTime, humaniseAction, shortTime } from "@/lib/format";

export const Route = createFileRoute("/_app/admin/activity")({
  component: PlatformActivityScreen,
});

/**
 * Everything everyone did, across every tenant, including us.
 *
 * This screen is the reason the audit log is append-only at the database level
 * rather than by convention: a surface that can read every action is only safe
 * if it cannot also remove one.
 */
function PlatformActivityScreen() {
  const [action, setAction] = useState("");

  const entries = useQuery({
    queryKey: ["platform-activity", action],
    queryFn: ({ signal }) =>
      api.get<{ entries: AuditEntry[] | null }>(
        "/admin/audit-log",
        { action: action || undefined, limit: 100 },
        signal,
      ),
  });

  const list = entries.data?.entries ?? [];

  return (
    <Panel
      title="Everything, everywhere"
      description="Across every organisation on this installation, including what we did."
      actions={
        <Input
          value={action}
          placeholder="Filter by action"
          className="h-8 w-56 text-[13px]"
          onChange={(e) => setAction(e.target.value)}
        />
      }
      padded={false}
    >
      {entries.isLoading ? (
        <div className="px-5 py-4">
          <TableSkeleton rows={6} columns={4} />
        </div>
      ) : entries.isError ? (
        <div className="px-5 py-4">
          <ErrorState error={entries.error} onRetry={() => void entries.refetch()} />
        </div>
      ) : list.length === 0 ? (
        <div className="px-5 py-4">
          <EmptyState
            title="Nothing matches"
            description={action ? `No action matches "${action}".` : "Nothing has happened yet."}
          />
        </div>
      ) : (
        <div className="px-5 py-3">
          <Table dense fixed>
            <thead>
              <Tr>
                <Th width="w-[22%]">What</Th>
                <Th width="w-[20%]">Who</Th>
                <Th width="w-[20%]">On what</Th>
                <Th width="w-[24%]">Why</Th>
                <Th width="w-[14%]" align="right">
                  When
                </Th>
              </Tr>
            </thead>
            <tbody>
              {list.map((entry) => (
                <Tr key={entry.id}>
                  {/* The raw action was a second line under the readable one,
                      which is most of why these rows were twenty pixels taller
                      than every other table's. It is the hover text now: the
                      people who need the exact string are the people who know
                      to look for it. */}
                  <Td clip title={entry.action}>{humaniseAction(entry.action)}</Td>
                  <Td clip title={entry.actor_label || entry.actor_type}>
                    <span className="text-[var(--text-secondary)]">
                      {entry.actor_label || entry.actor_type.replace(/_/g, " ")}
                    </span>
                  </Td>
                  <Td clip title={entry.target_label || entry.target_type}>
                    <span className="text-[var(--text-secondary)]">
                      {entry.target_label || entry.target_type}
                    </span>
                  </Td>
                  {/* Its own column rather than a third line. Every staff
                      action now carries one, so this is the column somebody
                      reads the log for. */}
                  <Td clip title={entry.reason || undefined}>
                    <span className="text-[var(--text-secondary)]">{entry.reason || "—"}</span>
                  </Td>
                  <Td align="right" title={absoluteTime(entry.created_at)}>
                    {shortTime(entry.created_at)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </Panel>
  );
}
